import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const EliteStackGrid = () => {
    const stack = [
        {
            id: 'frontend',
            title: 'Frontend Mastery',
            techs: [
                { name: 'React', icon: 'Atom', color: 'text-cyan-400' },
                { name: 'Next.js', icon: 'Triangle', color: 'text-white' },
                { name: 'Tailwind CSS', icon: 'Wind', color: 'text-sky-400' },
                { name: 'TypeScript', icon: 'Code2', color: 'text-blue-500' }
            ],
            description: 'Building high-performance, SEO-optimized user interfaces for SaaS and E-commerce platforms.'
        },
        {
            id: 'mobile',
            title: 'Mobile Innovation',
            techs: [
                { name: 'React Native', icon: 'Smartphone', color: 'text-blue-400' },
                { name: 'Expo', icon: 'Layers', color: 'text-white' },
                { name: 'Native Modules', icon: 'Cpu', color: 'text-emerald-400' }
            ],
            description: 'Developing seamless cross-platform mobile applications with native feel and performance.'
        },
        {
            id: 'backend',
            title: 'Backend & Infrastructure',
            techs: [
                { name: 'Node.js', icon: 'Server', color: 'text-green-500' },
                { name: 'Python', icon: 'Terminal', color: 'text-yellow-500' },
                { name: 'PostgreSQL', icon: 'Database', color: 'text-indigo-400' },
                { name: 'AWS / Vercel', icon: 'Cloud', color: 'text-orange-400' }
            ],
            description: 'Architecting scalable, secure server-side systems and real-time data pipelines.'
        }
    ];

    const capabilityStatement = "No matter the complexity or the stack required, our expertise spans the entire digital lifecycle. From initial architecture to deployment, we deliver enterprise-grade projects tailored to your specific business needs.";

    return (
        <section className="py-24 bg-slate-950 overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        Our <span className="text-gradient">Technology Stack</span>
                    </h2>
                    <p className="text-slate-400 max-w-2xl mx-auto">
                        We use modern, battle-tested frameworks to deliver high-performance custom applications.
                    </p>
                </div>
                <div className="grid lg:grid-cols-3 gap-8">
                    {stack.map((group, index) => (
                        <motion.div
                            key={group.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className="bg-white/5 border border-white/10 rounded-[2.5rem] p-10 flex flex-col h-full hover:bg-white/10 transition-all group"
                        >
                            <h3 className="text-2xl font-bold text-white mb-6 underline decoration-primary decoration-4 underline-offset-8 uppercase tracking-tight">
                                {group.title}
                            </h3>

                            <div className="flex flex-wrap gap-4 mb-8">
                                {group.techs.map((tech) => (
                                    <div key={tech.name} className="flex items-center space-x-2 bg-white/5 px-3 py-1.5 rounded-full border border-white/5 shadow-inner">
                                        <Icon name={tech.icon} size={14} className={tech.color} />
                                        <span className="text-sm font-bold text-white/80">{tech.name}</span>
                                    </div>
                                ))}
                            </div>

                            <p className="text-slate-400 text-sm leading-relaxed mb-auto">
                                {group.description}
                            </p>
                        </motion.div>
                    ))}
                </div>

                {/* Global Capability Callout */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="mt-16 bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-white/10 rounded-[3rem] p-12 text-center backdrop-blur-3xl"
                >
                    <div className="max-w-4xl mx-auto">
                        <div className="inline-flex items-center space-x-2 bg-white/5 border border-white/10 text-white/60 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em] mb-8">
                            <Icon name="Zap" size={14} className="text-yellow-400" />
                            <span>Full-Spectrum Versatility</span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold text-white leading-snug mb-8">
                            {capabilityStatement}
                        </h2>
                        <div className="flex flex-wrap justify-center gap-6 text-sm font-bold uppercase tracking-widest text-slate-500">
                            <span className="flex items-center"><Icon name="CheckCircle" size={16} className="mr-2 text-emerald-500" /> End-to-End Projects</span>
                            <span className="flex items-center"><Icon name="Zap" size={16} className="mr-2 text-yellow-500" /> High Performance Systems</span>
                            <span className="flex items-center"><Icon name="CheckCircle" size={16} className="mr-2 text-emerald-500" /> Senior Orchestration</span>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default EliteStackGrid;
